import { DrawerId, useDrawer } from 'common/components';
import { ROUTES } from 'common/constants';
import { usePermissionsBalance } from 'common/hooks/use-permissions-balance.hook';
import { OrganizationIcon } from 'common/icons';
import { useNavigateWithOrgAndFarm } from 'common/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useOrganization } from 'src/pages/organization';

import {
  CommercializationItemKeys,
  FeatureItemKeys,
  featureListData,
  FinancialItemKeys,
  OrdersAndPaymentsItemKeys,
  ResourcesItemKeys,
  RevisionItemKeys,
} from './menu-item/feature-list.data';
import { ItemDescription } from './menu-item/item-description.component';
import { MenuItemModel } from './menu-item/menu-item.model';
import { filterFeatureItems, getMenuItem } from './menu-item/menu-item.utils';
import { getFeatureKeyFromPathname } from './sider.navbar.util';

interface UseSiderNavbarProps {
  onClickMenu?: () => void;
  isMobile?: boolean;
}

export const useSiderNavbar = ({
  onClickMenu,
  isMobile = false,
}: UseSiderNavbarProps) => {
  const location = useLocation();
  const [current, setCurrent] = useState<
    | CommercializationItemKeys
    | FeatureItemKeys
    | ResourcesItemKeys
    | FinancialItemKeys
    | OrdersAndPaymentsItemKeys
    | RevisionItemKeys
    | undefined
  >(getFeatureKeyFromPathname(location.pathname));
  const { t } = useTranslation();
  const featureFlags = useFlags();
  const navigate = useNavigateWithOrgAndFarm();
  const { permissions } = usePermissionsBalance();
  const { entitlements, currentOrganization } = useOrganization();
  const { open: openDrawer } = useDrawer();

  const onMenuItemClick = useCallback(
    (route: string | undefined) => {
      route && navigate(route);
      !!onClickMenu && onClickMenu();
    },
    [navigate, onClickMenu],
  );

  const handleOnClickVoiceManagement = useCallback(() => {
    openDrawer(DrawerId.BalanceAi);
    onClickMenu?.();
  }, [onClickMenu, openDrawer]);

  const handleFeatureItems = useCallback((): MenuItemModel[] => {
    const ListMenu = Object.values(FeatureItemKeys)
      .filter((value) => {
        return filterFeatureItems(
          featureListData[value],
          featureFlags,
          isMobile,
          permissions,
          entitlements,
          currentOrganization,
        );
      })
      .map((value: string) => {
        const menuItem = getMenuItem({
          t,
          callbackClickFunction: onMenuItemClick,
          featureKey: value as FeatureItemKeys,
          isMobile,
          featureFlags,
          permissions,
          entitlements,
          currentOrganization,
        });
        if (value === FeatureItemKeys.VOICE_MANAGEMENT) {
          return {
            ...menuItem,
            onClick: handleOnClickVoiceManagement,
          } as MenuItemModel;
        }

        return menuItem;
      });

    if (isMobile) {
      return [
        {
          key: 'my_cropwise',
          label: (
            <ItemDescription
              name={t('my_cropwise.title')}
              description={t('my_cropwise.description')}
            />
          ),
          icon: <OrganizationIcon />,
          onClick: () => {
            navigate(ROUTES.MY_CROPWISE, null, null);
            !!onClickMenu && onClickMenu();
          },
        },
        ...ListMenu,
      ];
    }

    return [...ListMenu];
  }, [
    featureFlags,
    isMobile,
    permissions,
    t,
    onMenuItemClick,
    navigate,
    onClickMenu,
    handleOnClickVoiceManagement,
    entitlements,
    currentOrganization,
  ]);

  useEffect(() => {
    setCurrent(getFeatureKeyFromPathname(location.pathname));
  }, [location.pathname]);

  return {
    entitlements,
    handleFeatureItems,
    current,
  };
};
